/**
 * SessionDetailPage — iter117.
 *
 * Dedicated route at /groups/:groupId/sessions/:sessionId so users (and
 * notification deep-links) can land directly on a single session without
 * going through the Group → People → Sessions sub-tab drill-down.
 *
 * Contents:
 *   1. Sticky back button (returns to Group People → Sessions sub-tab).
 *   2. Hero card — title, status badge, countdown chip, date/time meta.
 *   3. RSVP block — confirmed roster + waitlist + guests with timestamps,
 *      I'm-In / Drop-Out buttons (members) and admin controls.
 *   4. Outcomes block — attendance counts + late tags + fines totals
 *      (visible once status is completed).
 *   5. Money trail — fund_transactions tied to this session (fines + fees).
 *   6. History strip — last 5 completed/cancelled sessions of the same
 *      group for context ("what usually happens").
 */
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowLeft, Calendar, Clock, MapPin, Users, UserCheck, UserX,
  CheckCircle2, XCircle, AlertTriangle, ClipboardCheck, Wallet,
  Pencil, Trash2, History, Receipt, Trophy, Flame, Sparkles,
  RotateCcw, Undo2,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useRealtime } from '../context/RealtimeContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import EmptyState from '../components/EmptyState';
import { EmptySessions } from '../components/illustrations';
import { formatNXC } from '../lib/formatNXC';
import useSessionLifecycle from '../hooks/useSessionLifecycle';
import useSessionAlerts from '../hooks/useSessionAlerts';
import useSessionPresence from '../lib/useSessionPresence';
import { playRsvpNudge, primeAudio } from '../lib/rsvpNudge';
import CountdownChip from '../components/CountdownChip';
import SessionStatusBadge from '../components/SessionStatusBadge';
import GuestFeesPanel from '../components/GuestFeesPanel';
import GuestLinkSheet from '../components/GuestLinkSheet';
function formatRsvpAt(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch { return ''; }
}

function formatTxnDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
}

const SESSION_EVENTS = [
  'rsvp_changed',
  'session_updated',
  'session_cancelled',
  'session_fee_charged',
  'rsvp_locked',
  'rsvp_admin_added',
  'rsvp_admin_removed',
  'session_needs_guests',
  'fine_issued',
];

export default function SessionDetailPage() {
  const { groupId, sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { onEvent } = useRealtime();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const fetchDetail = useCallback(async () => {
    try {
      const res = await api.get(`/sessions/${sessionId}/detail`);
      setData(res.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        // Most likely cause in the wild: production backend doesn't yet have
        // the iter117 /detail endpoint. Show a brief, friendly message and
        // fall back to the legacy deep-link so the user still lands on the
        // session — no dead-end. Once backend is redeployed, this never fires.
        toast.message('Refreshing the new session view…', {
          description: "We'll open the classic Sessions view for now.",
          duration: 2500,
        });
        navigate(`/groups/${groupId}?tab=sessions&focus=${sessionId}`, { replace: true });
      } else if (status === 403) {
        toast.error("You're not a member of this group");
        navigate('/groups');
      } else {
        toast.error('Failed to load session');
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId, groupId, navigate]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // SSE — refresh on any session/fund event that mentions this session.
  useEffect(() => {
    return onEvent(SESSION_EVENTS, (_type, payload) => {
      if (!payload) return;
      if (payload.session_id && payload.session_id !== sessionId) return;
      if (payload.group_id && payload.group_id !== groupId) return;
      fetchDetail();
    });
  }, [onEvent, sessionId, groupId, fetchDetail]);

  // Audible/haptic alerts for users RSVP'd "In" (reuses Home rail hook by
  // passing a single-element array with the rail-card shape).
  const railShape = useMemo(() => {
    if (!data?.session) return [];
    const s = data.session;
    return [{
      session_id: s.id,
      title: s.title,
      group_id: groupId,
      im_in: s.my_rsvp === 'confirmed',
      start_iso: buildStartIso(s),
    }];
  }, [data, groupId]);
  useSessionAlerts(railShape);

  // iter138 — 3-tier live presence (online dots / viewing rings / considering pulses).
  // Only enable once the user is confirmed to be a member (data loaded successfully).
  const presence = useSessionPresence(sessionId, { enabled: !!data?.session });

  if (loading) {
    return (
      <div className="px-4 py-3 space-y-3" data-testid="session-detail-loading">
        <Skeleton className="h-9 w-32" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
      </div>
    );
  }
  if (!data) return null;

  const { session, group, history, related_txns, fines_total, fees_total } = data;
  const isAdmin = !!group?.is_admin;
  const durationMin = group?.session_defaults?.duration_minutes || 120;
  const liveStyle = group?.session_defaults?.live_indicator_style || 'red_pulse';
  const canShareGuestLink = isAdmin && !['cancelled', 'completed'].includes(session?.status);

  return (
    <div className="px-4 py-3 pb-24 space-y-4" data-testid="session-detail-page">
      {/* Back nav */}
      <div className="flex items-center justify-between sticky top-0 z-10 -mx-4 px-4 py-2 bg-background/85 backdrop-blur-md border-b border-border/40">
        <button
          onClick={() => navigate(`/groups/${groupId}?tab=sessions&focus=${sessionId}`)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          data-testid="session-detail-back"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="truncate max-w-[10rem]">{group?.name || 'Back'}</span>
        </button>
        {isAdmin && (
          <Link
            to={`/groups/${groupId}?tab=sessions&focus=${sessionId}`}
            className="text-xs text-muted-foreground hover:text-primary"
            data-testid="session-detail-open-in-tab"
          >
            Open in Sessions tab
          </Link>
        )}
      </div>

      <HeroCard
        session={session}
        durationMin={durationMin}
        liveStyle={liveStyle}
        finesTotal={fines_total}
        feesTotal={fees_total}
      />

      <RsvpRoster
        session={session}
        isAdmin={isAdmin}
        groupId={groupId}
        acting={acting}
        setActing={setActing}
        userId={user?.id}
        presence={presence}
        onChange={fetchDetail}
      />

      {session.status === 'completed' && (
        <OutcomesCard session={session} finesTotal={fines_total} feesTotal={fees_total} />
      )}

      {isAdmin && session.status === 'completed' && (
        <AdminRecoveryCard
          session={session}
          groupId={groupId}
          feesTotal={fees_total}
          onChange={fetchDetail}
        />
      )}

      {/* iter197 — Guest fees panel (only when session has external guests) */}
      {session.external_guests && session.external_guests.length > 0 && (
        <GuestFeesPanel
          sessionId={session.id}
          isAdmin={isAdmin}
          onChange={fetchDetail}
        />
      )}

      {/* iter197 — Admin: mint a guest RSVP link */}
      {canShareGuestLink && (
        <ShareGuestLinkButton
          groupId={groupId}
          sessionId={session.id}
          sessionTitle={session.title}
          groupName={group?.name}
        />
      )}

      <MoneyTrail txns={related_txns} groupId={groupId} />

      <HistoryStrip history={history} groupId={groupId} />
    </div>
  );
}

/* ============================================================== */
/* Helpers                                                          */
/* ============================================================== */
function buildStartIso(s) {
  if (!s?.date || !s?.start_time) return null;
  try {
    const [yy, mo, dd] = s.date.split('-').map((x) => parseInt(x, 10) || 0);
    const [hh, mm] = String(s.start_time).split(':').map((x) => parseInt(x, 10) || 0);
    // IST → UTC offset (-5:30)
    return new Date(Date.UTC(yy, mo - 1, dd, hh - 5, mm - 30)).toISOString();
  } catch { return null; }
}

/* ============================================================== */
/* Hero                                                              */
/* ============================================================== */
function HeroCard({ session, durationMin, liveStyle, finesTotal, feesTotal }) {
  const lifecycle = useSessionLifecycle(session, { durationMinutes: durationMin });

  const tone =
    lifecycle.isLive ? 'live' :
    lifecycle.phase === 'rsvp_open' ? 'open' :
    lifecycle.phase === 'cancelled' ? 'cancelled' :
    'default';

  const toneClass = {
    live: liveStyle === 'green_glow'
      ? 'border-emerald-500/60 bg-emerald-50/40 dark:bg-emerald-950/30'
      : 'border-red-500/60 bg-red-50/40 dark:bg-red-950/25 shadow-[0_0_22px_-6px_rgba(239,68,68,0.55)]',
    open: 'border-emerald-500/40 bg-emerald-50/30 dark:bg-emerald-950/15',
    cancelled: 'border-rose-500/30 bg-rose-50/30 dark:bg-rose-950/15 opacity-80',
    default: 'border-border',
  }[tone];

  const upcomingChip = (() => {
    if (!lifecycle.isUpcoming) return null;
    if (lifecycle.phase === 'scheduled' && lifecycle.secondsToRsvpOpen != null) {
      return <CountdownChip seconds={lifecycle.secondsToRsvpOpen} prefix="RSVP opens in" testId="hero-rsvp-countdown" />;
    }
    if (lifecycle.secondsToStart != null && lifecycle.secondsToStart > 0) {
      return <CountdownChip seconds={lifecycle.secondsToStart} prefix="Starts in" testId="hero-start-countdown" />;
    }
    return null;
  })();

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      data-testid="session-detail-hero"
    >
      <Card className={`p-5 rounded-2xl border-2 ${toneClass}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <SessionStatusBadge lifecycle={lifecycle} />
              {session.num_courts > 1 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-muted font-semibold uppercase tracking-wider">
                  {session.num_courts} courts
                </span>
              )}
              {session.fees_charged && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary font-bold uppercase">
                  Fees collected
                </span>
              )}
              {session.fines_issued && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-700 dark:text-rose-300 font-bold uppercase">
                  Fines issued
                </span>
              )}
            </div>
            <h1 className="font-display text-2xl font-bold mt-2 leading-tight" data-testid="session-detail-title">
              {session.title}
            </h1>
            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {new Date(session.date).toLocaleDateString('en-IN', {
                  weekday: 'long', month: 'short', day: 'numeric',
                })}
              </span>
              {session.start_time && (
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {session.start_time}
                </span>
              )}
              {session.num_courts > 0 && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {session.num_courts} court{session.num_courts === 1 ? '' : 's'}
                </span>
              )}
            </p>
            {upcomingChip && (
              <p className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1">
                ⏱ {upcomingChip}
              </p>
            )}
            {lifecycle.isLive && (
              <p className="text-xs font-bold text-red-600 dark:text-red-300 mt-2 inline-flex items-center gap-1.5">
                <Flame className="w-3.5 h-3.5" />
                LIVE NOW — game on
              </p>
            )}
          </div>
        </div>

        {/* mini-stats row */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          <Stat
            icon={<Users className="w-3.5 h-3.5" />}
            label="Confirmed"
            value={`${session.confirmed_ids?.length || 0}/${session.max_players || '?'}`}
            testId="stat-confirmed"
          />
          <Stat
            icon={<Wallet className="w-3.5 h-3.5" />}
            label="Fees"
            value={feesTotal > 0 ? formatNXC(feesTotal) : '—'}
            testId="stat-fees"
            tone="primary"
          />
          <Stat
            icon={<AlertTriangle className="w-3.5 h-3.5" />}
            label="Fines"
            value={finesTotal > 0 ? formatNXC(finesTotal) : '—'}
            testId="stat-fines"
            tone={finesTotal > 0 ? 'rose' : 'muted'}
          />
        </div>
      </Card>
    </motion.div>
  );
}

function Stat({ icon, label, value, tone = 'default', testId }) {
  const toneCls = {
    default: 'text-foreground',
    primary: 'text-primary',
    rose: 'text-rose-600 dark:text-rose-300',
    muted: 'text-muted-foreground',
  }[tone];
  return (
    <div className="rounded-xl bg-card/70 border border-border/50 p-2.5 text-center" data-testid={testId}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1 justify-center">
        {icon}{label}
      </p>
      <p className={`text-base font-display font-bold tabular-nums mt-0.5 ${toneCls}`}>{value}</p>
    </div>
  );
}

/* ============================================================== */
/* RSVP roster                                                       */
/* ============================================================== */
function RsvpRoster({ session, isAdmin, groupId, acting, setActing, userId, presence, onChange }) {
  const online = presence?.online || new Set();
  const viewing = presence?.viewing || [];
  const considering = presence?.considering || [];
  const markConsidering = presence?.markConsidering;

  const viewingIds = useMemo(() => new Set(viewing.map((v) => v.user_id)), [viewing]);
  const consideringIds = useMemo(
    () => new Set(considering.map((c) => c.user_id)),
    [considering],
  );

  // Tooltip-friendly maps from user_id → name (for the summary strip).
  const otherViewing = viewing.filter((v) => v.user_id !== userId);
  const otherConsidering = considering.filter((c) => c.user_id !== userId);

  const isOpenForRsvp = session.status === 'rsvp_open';
  const myConfirmedIdx = session.confirmed_ids?.indexOf(userId);
  const myWaitlistIdx = session.waitlist_ids?.indexOf(userId);
  const isMeConfirmed = myConfirmedIdx >= 0;
  const isMeWaitlist = myWaitlistIdx >= 0;
  const meIsUndecided = isOpenForRsvp && !isMeConfirmed && !isMeWaitlist;

  // iter139 — FOMO nudge: when *another* member starts considering the same
  // slot AND I haven't RSVP'd yet, fire a sub-perceptual haptic + tap. Track
  // the set of considerers we've already nudged for so we don't re-fire on
  // their heartbeat re-broadcasts.
  const nudgedRef = useRef(new Set());
  useEffect(() => {
    if (!meIsUndecided) {
      nudgedRef.current.clear();
      return;
    }
    let fired = false;
    otherConsidering.forEach((c) => {
      if (!nudgedRef.current.has(c.user_id)) {
        nudgedRef.current.add(c.user_id);
        if (!fired) {
          playRsvpNudge();
          fired = true;
        }
      }
    });
    // Drop entries that are no longer considering so the next time they
    // start, they'll nudge again.
    const stillSet = new Set(otherConsidering.map((c) => c.user_id));
    nudgedRef.current.forEach((uid) => {
      if (!stillSet.has(uid)) nudgedRef.current.delete(uid);
    });
  }, [otherConsidering, meIsUndecided]);

  const handleRsvp = async (action) => {
    if (acting) return;
    setActing(`${session.id}:${action}`);
    try {
      const res = await api.post(`/sessions/${session.id}/rsvp`, { action });
      if (res.data?.fine_applied) {
        toast.warning(`RSVP dropped. Late-cancel fine of ${formatNXC(res.data.fine_applied.amount)} charged.`);
      } else {
        toast.success(action === 'in' ? "You're IN" : 'Marked OUT');
      }
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'RSVP failed');
    } finally { setActing(null); }
  };

  const isCancelled = session.status === 'cancelled';
  const isCompleted = session.status === 'completed';
  const guestCount = session.external_guests?.length || 0;
  const confirmedCount = session.confirmed_ids?.length || 0;
  const waitlistCount = session.waitlist_ids?.length || 0;
  const hasRsvp = !!session.rsvp_opens_at;

  if (!hasRsvp) {
    return (
      <Card className="p-4 rounded-2xl border border-border/50" data-testid="rsvp-roster-card">
        <div className="text-sm text-muted-foreground">
          No RSVP enabled for this session. Admins can edit it to enable sign-ups.
        </div>
      </Card>
    );
  }

  // current user's slot — used to render the "You're #3 of 6" callout.
  // (myConfirmedIdx / myWaitlistIdx already computed above for FOMO logic.)

  return (
    <Card className="p-4 rounded-2xl border border-border/50 space-y-3" data-testid="rsvp-roster-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display font-bold inline-flex items-center gap-1.5">
          <Users className="w-4 h-4" />
          Roster
        </h3>
        <span className="text-xs text-muted-foreground tabular-nums">
          {confirmedCount + guestCount}/{session.max_players || '?'} confirmed
          {waitlistCount > 0 && ` · waitlist ${waitlistCount}`}
        </span>
      </div>

      <PresenceStrip
        online={online}
        otherViewing={otherViewing}
        otherConsidering={otherConsidering}
        groupMemberCount={(session.confirmed_ids?.length || 0) + (session.waitlist_ids?.length || 0)}
      />

      {(isMeConfirmed || isMeWaitlist) && !isCancelled && (
        <div className={`text-xs px-3 py-2 rounded-lg font-semibold inline-flex items-center gap-1.5 ${
          isMeConfirmed
            ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
            : 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
        }`} data-testid="my-rsvp-callout">
          {isMeConfirmed ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
          {isMeConfirmed
            ? `You're confirmed · #${myConfirmedIdx + 1} of ${confirmedCount}`
            : `You're on the waitlist · #${myWaitlistIdx + 1}`}
          {(session.rsvp_timestamps?.[userId]) && (
            <span className="opacity-70">· locked in @ {formatRsvpAt(session.rsvp_timestamps[userId])}</span>
          )}
        </div>
      )}

      {/* Member RSVP buttons */}
      {(isOpenForRsvp || (session.rsvp_locked && !isCancelled && !isCompleted && (isMeConfirmed || isMeWaitlist))) && (
        <div className="space-y-2">
          {meIsUndecided && otherConsidering.length > 0 && (
            <FomoNudge
              count={otherConsidering.length}
              names={otherConsidering.map((c) => c.user_name).filter(Boolean)}
            />
          )}
          <div className="flex gap-2">
            {isOpenForRsvp && !isMeConfirmed && !isMeWaitlist && (
              <Button
                size="sm"
                onClick={() => handleRsvp('in')}
                onPointerDown={primeAudio}
                onMouseEnter={markConsidering}
                onFocus={markConsidering}
                onTouchStart={markConsidering}
                disabled={acting === `${session.id}:in`}
                data-testid="detail-rsvp-in"
                className="rounded-full text-xs flex-1"
              >
                <UserCheck className="w-3.5 h-3.5 mr-1" /> I'm IN
              </Button>
            )}
            {(isMeConfirmed || isMeWaitlist) && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleRsvp('out')}
                onPointerDown={primeAudio}
                onMouseEnter={markConsidering}
                onFocus={markConsidering}
                onTouchStart={markConsidering}
                disabled={acting === `${session.id}:out`}
                data-testid="detail-rsvp-out"
                className="rounded-full text-xs flex-1"
              >
                <UserX className="w-3.5 h-3.5 mr-1" /> Drop OUT
                {isMeWaitlist && ' (waitlist)'}
                {session.rsvp_locked && isMeConfirmed && ' ⚠️'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Confirmed chips */}
      {confirmedCount > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-1.5">Confirmed</p>
          <div className="flex flex-wrap gap-1.5" data-testid="confirmed-chips">
            {session.confirmed_names?.map((name, i) => {
              const uid = session.confirmed_ids?.[i];
              const at = formatRsvpAt(session.confirmed_rsvp_ats?.[i]);
              return (
                <PresenceChip
                  key={`c${i}`}
                  testId={`confirmed-chip-${i}`}
                  index={i + 1}
                  name={name}
                  meta={at}
                  tone="emerald"
                  isOnline={uid ? online.has(uid) : false}
                  isViewing={uid ? viewingIds.has(uid) : false}
                  isConsidering={uid ? consideringIds.has(uid) : false}
                  isSelf={uid && uid === userId}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Guest chips */}
      {guestCount > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-1.5">Guests</p>
          <div className="flex flex-wrap gap-1">
            {session.external_guests?.map((g) => (
              <span
                key={g.id}
                className="text-xs px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-700 dark:text-blue-300 font-medium"
              >
                {g.name} (guest)
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Waitlist chips */}
      {waitlistCount > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-1.5">Waitlist</p>
          <div className="flex flex-wrap gap-1.5">
            {session.waitlist_names?.map((name, i) => {
              const uid = session.waitlist_ids?.[i];
              const at = formatRsvpAt(session.waitlist_rsvp_ats?.[i]);
              return (
                <PresenceChip
                  key={`w${i}`}
                  testId={`waitlist-chip-${i}`}
                  index={i + 1}
                  name={name}
                  meta={at}
                  tone="amber"
                  prefix="⏳"
                  isOnline={uid ? online.has(uid) : false}
                  isViewing={uid ? viewingIds.has(uid) : false}
                  isConsidering={uid ? consideringIds.has(uid) : false}
                  isSelf={uid && uid === userId}
                />
              );
            })}
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="pt-2 border-t border-border/40 text-xs text-muted-foreground">
          Tip — admin actions (lock, add members, charge fees) are still on the full Sessions tab.
          <Link
            to={`/groups/${groupId}?tab=sessions&focus=${session.id}`}
            className="ml-1 text-primary font-medium hover:underline"
            data-testid="session-detail-admin-link"
          >
            Open admin controls →
          </Link>
        </div>
      )}
    </Card>
  );
}

/* ============================================================== */
/* Presence chip + summary strip (iter138)                           */
/* ============================================================== */
function FomoNudge({ count, names }) {
  const label = count === 1
    ? `${names[0] || 'Someone'} is deciding right now`
    : `${count} others are deciding right now${names.length ? ` · ${names.slice(0, 2).join(', ')}${count > 2 ? '…' : ''}` : ''}`;
  return (
    <motion.div
      key={count}
      initial={{ opacity: 0, y: -2, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 380, damping: 22 }}
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-violet-500/15 text-violet-700 dark:text-violet-300 ring-1 ring-violet-500/30 shadow-[0_0_14px_-4px_rgba(139,92,246,0.55)]"
      data-testid="fomo-nudge"
      title={names.join(', ')}
    >
      <span className="relative inline-flex w-1.5 h-1.5">
        <span className="absolute inset-0 rounded-full bg-violet-500" />
        <span className="absolute inset-0 rounded-full bg-violet-400 animate-ping opacity-70" />
      </span>
      <span>{label}</span>
      <span className="text-xs opacity-70">— lock in?</span>
    </motion.div>
  );
}

function PresenceChip({
  testId, index, name, meta, tone = 'emerald', prefix,
  isOnline, isViewing, isConsidering, isSelf,
}) {
  const toneClass = tone === 'amber'
    ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
    : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';

  // Layered visual treatments:
  //  • viewing  → violet ring around chip
  //  • considering → animated violet pulse halo around chip
  //  • online   → tiny green dot at top-right of the avatar circle
  const ringClass = isConsidering
    ? 'ring-2 ring-violet-500/70 animate-pulse shadow-[0_0_10px_-2px_rgba(139,92,246,0.55)]'
    : isViewing
      ? 'ring-2 ring-violet-400/60'
      : '';

  const liveLabel = isConsidering
    ? 'considering RSVP right now'
    : isViewing
      ? 'viewing this session'
      : isOnline
        ? 'online'
        : '';

  return (
    <span
      data-testid={testId}
      data-presence={
        isConsidering ? 'considering' : isViewing ? 'viewing' : isOnline ? 'online' : 'offline'
      }
      title={[
        index ? `#${index}` : '',
        meta ? `at ${meta}` : '',
        liveLabel,
      ].filter(Boolean).join(' · ')}
      className={
        `relative text-xs pl-2 pr-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 ` +
        `${toneClass} ${ringClass} transition-all`
      }
    >
      {/* presence dot */}
      <span className="relative inline-flex w-2 h-2">
        {isOnline && (
          <>
            <span className="absolute inset-0 rounded-full bg-emerald-500 opacity-90" />
            <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
          </>
        )}
        {!isOnline && (
          <span className="absolute inset-0 rounded-full bg-muted-foreground/30" />
        )}
      </span>
      {prefix && <span aria-hidden>{prefix}</span>}
      {index != null && <span className="font-bold text-xs opacity-70">#{index}</span>}
      <span className="truncate max-w-[7rem]">{name}{isSelf && ' (you)'}</span>
      {meta && <span className="text-xs tabular-nums opacity-70">· {meta}</span>}
    </span>
  );
}

function PresenceStrip({ online, otherViewing, otherConsidering }) {
  const onlineCount = online?.size || 0;
  const viewingCount = otherViewing?.length || 0;
  const consideringCount = otherConsidering?.length || 0;

  if (onlineCount === 0 && viewingCount === 0 && consideringCount === 0) {
    return null;
  }

  const viewingNames = otherViewing.slice(0, 3).map((v) => v.user_name).filter(Boolean);
  const considerNames = otherConsidering.slice(0, 3).map((c) => c.user_name).filter(Boolean);

  return (
    <div
      className="text-xs flex items-center gap-3 flex-wrap text-muted-foreground"
      data-testid="presence-strip"
    >
      {onlineCount > 0 && (
        <span className="inline-flex items-center gap-1" data-testid="presence-online-count">
          <span className="relative inline-flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-emerald-500" />
            <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
          </span>
          <span><strong className="text-foreground">{onlineCount}</strong> online</span>
        </span>
      )}
      {viewingCount > 0 && (
        <span
          className="inline-flex items-center gap-1"
          data-testid="presence-viewing-count"
          title={viewingNames.join(', ')}
        >
          <span className="w-2 h-2 rounded-full ring-2 ring-violet-400/60 bg-violet-400/40" />
          <span>
            <strong className="text-foreground">{viewingCount}</strong> viewing
            {viewingNames.length > 0 && ` · ${viewingNames.join(', ')}${viewingCount > viewingNames.length ? ` +${viewingCount - viewingNames.length}` : ''}`}
          </span>
        </span>
      )}
      {consideringCount > 0 && (
        <span
          className="inline-flex items-center gap-1"
          data-testid="presence-considering-count"
          title={considerNames.join(', ')}
        >
          <span className="w-2 h-2 rounded-full bg-violet-500 animate-pulse" />
          <span>
            <strong className="text-foreground">{consideringCount}</strong> deciding now
            {considerNames.length > 0 && ` · ${considerNames.join(', ')}${consideringCount > considerNames.length ? ` +${consideringCount - considerNames.length}` : ''}`}
          </span>
        </span>
      )}
    </div>
  );
}

/* ============================================================== */
/* Outcomes (completed sessions only)                                */
/* ============================================================== */
function OutcomesCard({ session, finesTotal, feesTotal }) {
  const presentCount = session.present_names?.length || 0;
  const absentCount = session.absent_names?.length || 0;
  const lateCount = (session.late_under_names?.length || 0) + (session.late_above_names?.length || 0);

  return (
    <Card className="p-4 rounded-2xl border border-border/50 space-y-3" data-testid="outcomes-card">
      <h3 className="font-display font-bold inline-flex items-center gap-1.5">
        <Trophy className="w-4 h-4" />
        Outcomes
      </h3>
      <div className="grid grid-cols-2 gap-2">
        <Stat icon={<UserCheck className="w-3.5 h-3.5" />} label="Present" value={String(presentCount)} tone="primary" testId="outcome-present" />
        <Stat icon={<UserX className="w-3.5 h-3.5" />} label="Absent" value={String(absentCount)} tone={absentCount > 0 ? 'rose' : 'muted'} testId="outcome-absent" />
      </div>
      {lateCount > 0 && (
        <div className="text-xs">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-1">Late arrivals</p>
          <div className="flex flex-wrap gap-1">
            {session.late_under_names?.map((n, i) => (
              <span key={`lu${i}`} className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300">
                {n} (≤ threshold)
              </span>
            ))}
            {session.late_above_names?.map((n, i) => (
              <span key={`la${i}`} className="px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-700 dark:text-rose-300">
                {n} (late)
              </span>
            ))}
          </div>
        </div>
      )}
      {absentCount > 0 && (
        <div className="text-xs">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-1">Absentees</p>
          <p className="text-muted-foreground">{session.absent_names?.join(', ')}</p>
        </div>
      )}
      {(feesTotal > 0 || finesTotal > 0) && (
        <p className="text-xs text-muted-foreground">
          This session contributed{' '}
          {feesTotal > 0 && <span className="text-primary font-semibold">{formatNXC(feesTotal)} in fees</span>}
          {feesTotal > 0 && finesTotal > 0 && ' + '}
          {finesTotal > 0 && <span className="text-rose-600 dark:text-rose-300 font-semibold">{formatNXC(finesTotal)} in fines</span>}
          {' '}to the group fund.
        </p>
      )}
    </Card>
  );
}

/* ============================================================== */
/* Money trail (related fund_transactions)                            */
/* ============================================================== */
function MoneyTrail({ txns, groupId }) {
  if (!txns || txns.length === 0) return null;

  return (
    <Card className="p-4 rounded-2xl border border-border/50 space-y-2" data-testid="money-trail-card">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold inline-flex items-center gap-1.5">
          <Receipt className="w-4 h-4" />
          Money trail
        </h3>
        <Link
          to={`/groups/${groupId}?tab=ledger`}
          className="text-xs text-muted-foreground hover:text-primary"
          data-testid="money-trail-ledger-link"
        >
          Full ledger →
        </Link>
      </div>
      <div className="divide-y divide-border/40">
        {txns.map((t) => {
          const isFine = t.type === 'fine';
          const isFee = t.type === 'session_fee';
          const reversed = !!t.reversed;
          const Icon = isFine ? AlertTriangle : isFee ? Wallet : Sparkles;
          const tone = reversed
            ? 'text-muted-foreground line-through'
            : isFine
              ? 'text-rose-600 dark:text-rose-300'
              : 'text-primary';
          return (
            <div key={t.id} className="py-2 flex items-center justify-between gap-2 text-sm" data-testid={`txn-${t.id}`}>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Icon className={`w-4 h-4 shrink-0 ${tone}`} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{t.user_name || t.note || t.type}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {t.note || (isFine ? `${t.fine_type || 'Fine'}` : isFee ? 'Session fee' : t.type)}
                    {' · '}
                    {formatTxnDate(t.created_at)}
                  </p>
                </div>
              </div>
              <span className={`font-display font-bold tabular-nums shrink-0 ${tone}`}>
                {formatNXC(t.amount || 0)}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ============================================================== */
/* History strip                                                      */
/* ============================================================== */
function HistoryStrip({ history, groupId }) {
  const navigate = useNavigate();
  if (!history || history.length === 0) {
    return (
      <Card className="p-4 rounded-2xl border-dashed border-border/50" data-testid="history-empty">
        <EmptyState
          illustration={EmptySessions}
          title="No past sessions yet"
          description="Once a few games wrap up, you'll see the recent history here."
          size="sm"
          card={false}
          testId="no-history"
        />
      </Card>
    );
  }
  return (
    <Card className="p-4 rounded-2xl border border-border/50 space-y-2" data-testid="history-strip">
      <h3 className="font-display font-bold inline-flex items-center gap-1.5">
        <History className="w-4 h-4" />
        Recent sessions
      </h3>
      <div className="space-y-1.5">
        {history.map((h) => {
          const pct = h.max_players ? Math.min(100, Math.round((h.present_count / h.max_players) * 100)) : 0;
          const isCancelled = h.status === 'cancelled';
          return (
            <button
              key={h.id}
              onClick={() => navigate(`/groups/${groupId}/sessions/${h.id}`)}
              className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-muted/50 transition-colors text-left"
              data-testid={`history-row-${h.id}`}
            >
              <div className="w-10 h-10 rounded-xl bg-muted/60 flex flex-col items-center justify-center shrink-0">
                <span className="text-xs uppercase font-bold text-muted-foreground">
                  {new Date(h.date).toLocaleDateString('en-IN', { month: 'short' })}
                </span>
                <span className="text-sm font-display font-bold leading-none">
                  {new Date(h.date).getDate()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate text-sm">
                  {h.title}
                  {isCancelled && <span className="ml-1 text-xs text-rose-600 dark:text-rose-300 font-bold">· CANCELLED</span>}
                </p>
                <p className="text-xs text-muted-foreground">
                  {h.start_time}{' · '}
                  {isCancelled
                    ? 'no game'
                    : `${h.present_count} present · ${h.absent_count} absent`}
                  {h.fines_issued && ' · fined'}
                  {h.fees_charged && ' · fees ✓'}
                </p>
              </div>
              {!isCancelled && h.max_players > 0 && (
                <div className="shrink-0 flex flex-col items-end">
                  <div className="w-12 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full ${pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground tabular-nums mt-0.5">
                    {h.present_count}/{h.max_players}
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </Card>
  );
}


/* ============================================================== */
/* AdminRecoveryCard — iter186                                      */
/* "Oops"-handler for completed sessions. Two actions:              */
/*   1. Reverse all session fees (refund every attendee)            */
/*   2. Re-open session (clear attendance so admin can re-mark)     */
/* ============================================================== */
function ShareGuestLinkButton({ groupId, sessionId, sessionTitle, groupName }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="share-guest-rsvp-link-btn"
        className="w-full rounded-2xl border-2 border-dashed border-violet-500/40 bg-violet-500/5 hover:bg-violet-500/10 transition-colors px-4 py-3 flex items-center gap-3 text-left"
      >
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-violet-500/15 text-violet-600 dark:text-violet-300 shrink-0">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Share a guest RSVP link</p>
          <p className="text-xs text-muted-foreground truncate">No SplitEasy account required · 48h link</p>
        </div>
      </button>
      <GuestLinkSheet
        open={open}
        onClose={() => setOpen(false)}
        scope="rsvp"
        groupId={groupId}
        sessionId={sessionId}
        sessionTitle={sessionTitle}
        groupName={groupName}
      />
    </>
  );
}


function AdminRecoveryCard({ session, groupId, feesTotal, onChange }) {
  const [busy, setBusy] = useState(null); // 'fees' | 'reopen' | null

  const handleReverseFees = async () => {
    if (!window.confirm(`Reverse ALL session fees for "${session.title}"?\n\nThis will credit each attendee's ₹ back and deduct the same amount from the group fund. The original fee rows are kept but marked as reversed. This action is logged.`)) return;
    setBusy('fees');
    try {
      const res = await api.post(`/sessions/${session.id}/reverse-session-fees`);
      toast.success(res.data.message || 'Session fees reversed');
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reversal failed');
    } finally {
      setBusy(null);
    }
  };

  const handleReopen = async () => {
    if (!window.confirm(`Re-open "${session.title}"?\n\nThis will:\n• Clear attendance (present / absent / late tags)\n• Mark the session as RSVP-ready again\n• KEEP any fines or fees already issued — reverse them separately if needed\n\nUse this to fix mistakes after Mark Attendance.`)) return;
    setBusy('reopen');
    try {
      const res = await api.post(`/sessions/${session.id}/reopen`);
      toast.success(res.data.message || 'Session reopened');
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-open failed');
    } finally {
      setBusy(null);
    }
  };

  const hasFeesToReverse = (feesTotal || 0) > 0;

  return (
    <Card className="p-4 rounded-2xl border-2 border-dashed border-amber-500/40 bg-amber-500/5" data-testid="admin-recovery-card">
      <div className="flex items-start gap-2.5 mb-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-sm">Admin recovery actions</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Made a mistake marking attendance or charging fees? Undo here. Each action is logged.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleReverseFees}
          disabled={busy !== null || !hasFeesToReverse}
          data-testid="reverse-session-fees-btn"
          className="rounded-full h-10 text-xs justify-start"
        >
          <Undo2 className="w-3.5 h-3.5 mr-1.5" />
          {busy === 'fees' ? 'Reversing…' : hasFeesToReverse ? `Reverse all fees (₹${Math.round(feesTotal)})` : 'No fees to reverse'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleReopen}
          disabled={busy !== null}
          data-testid="reopen-session-btn"
          className="rounded-full h-10 text-xs justify-start"
        >
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
          {busy === 'reopen' ? 'Re-opening…' : 'Re-open & re-mark'}
        </Button>
      </div>
    </Card>
  );
}
